import type { Action } from '../types';

/** Status (locais e legados) que indicam uma ação finalizada — não geram alerta de atraso. */
const FINALIZED_STATUSES = new Set(['done', 'completed', 'canceled', 'cancelled', 'concluido', 'Concluído']);

/**
 * Parseia com segurança a data de prazo de uma ação a partir de qualquer formato comum:
 * - ISO curto: "AAAA-MM-DD"
 * - ISO completo: "AAAA-MM-DDTHH:mm:ss..."
 * - BR: "DD/MM/AAAA"
 * Retorna a data zerada (00:00:00) ou null se inválida.
 */
export function parseActionDeadline(raw: unknown): Date | null {
  try {
    if (!raw || typeof raw !== 'string') return null;
    const s = raw.trim();
    if (!s) return null;

    // Formato BR: DD/MM/AAAA
    const br = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (br) {
      const d = new Date(Number(br[3]), Number(br[2]) - 1, Number(br[1]));
      if (isNaN(d.getTime())) return null;
      d.setHours(0, 0, 0, 0);
      return d;
    }

    // ISO (curto ou completo) ou qualquer string parsável
    const ts = Date.parse(/^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T00:00:00` : s);
    if (!Number.isFinite(ts)) return null;
    const d = new Date(ts);
    d.setHours(0, 0, 0, 0);
    return d;
  } catch {
    return null;
  }
}

/**
 * Indica se uma ação está atrasada: prazo (ou deadline) estritamente anterior a hoje
 * e status não finalizado. Lê `prazo` com fallback para `deadline`.
 */
export function isActionOverdue(action: Pick<Action, 'status' | 'prazo' | 'deadline'>): boolean {
  if (action.status && FINALIZED_STATUSES.has(action.status)) return false;
  const deadline = parseActionDeadline(action.prazo) ?? parseActionDeadline(action.deadline);
  if (!deadline) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return deadline < today;
}
